import React, { useEffect, useId, useRef, useState } from 'react';

const MARGE = 8;   // respiration minimale entre la bulle et le bord de l'écran

/**
 * Bulle d'aide sur une notion technique.
 *
 * Pas un attribut `title` : il n'apparaît qu'au survol, donc jamais sur un
 * téléphone - or c'est là que l'application sert le plus. Il est en outre
 * annoncé de façon inégale par les lecteurs d'écran et ne se met pas en forme.
 *
 * C'est donc un bouton bascule, ouvert au clic comme au clavier, refermé par
 * Échap ou par un clic à l'extérieur. Le contenu est lié au bouton par
 * `aria-describedby` afin d'être annoncé, et non simplement affiché.
 */
export default function Info({ titre, children }) {
  const [ouvert, setOuvert] = useState(false);
  const [ancrage, setAncrage] = useState({ cote: 'left', largeurMax: 320 });
  const zone = useRef(null);
  const id = useId();

  /**
   * La bulle s'ancre du côté où il reste de la place.
   *
   * Ancrée à gauche en toutes circonstances, elle débordait de 64 px dès que
   * le bouton était proche du bord droit - constaté à 390 px de large.
   * Une largeur maximale ne suffit pas : elle borne la bulle sans rien savoir
   * de l'endroit d'où elle part.
   *
   * On mesure donc l'espace disponible de chaque côté, on retient le plus
   * large, et on borne la bulle à cet espace. Le débordement devient
   * impossible par construction, quelle que soit la position du bouton.
   */
  const ancrer = () => {
    const r = zone.current?.getBoundingClientRect();
    if (!r) return;
    const aDroite = window.innerWidth - r.left - MARGE;   // si ancrée à gauche
    const aGauche = r.right - MARGE;                      // si ancrée à droite
    const cote = aDroite >= aGauche ? 'left' : 'right';
    setAncrage({ cote, largeurMax: Math.max(180, Math.min(320, cote === 'left' ? aDroite : aGauche)) });
  };

  useEffect(() => {
    if (!ouvert) return;
    const auClavier = (e) => { if (e.key === 'Escape') setOuvert(false); };
    // `pointerdown` et non `click` : sur un bouton voisin, `click` se
    // déclencherait après le rendu et refermerait la bulle qu'on vient d'ouvrir.
    const auPointeur = (e) => { if (!zone.current?.contains(e.target)) setOuvert(false); };
    document.addEventListener('keydown', auClavier);
    document.addEventListener('pointerdown', auPointeur);
    return () => {
      document.removeEventListener('keydown', auClavier);
      document.removeEventListener('pointerdown', auPointeur);
    };
  }, [ouvert]);

  return (
    // `lineHeight: 0` : sans cela l'enveloppe ajoutait 1 px d'interligne à
    // l'étiquette qui la contient, et le contrôle en dessous descendait
    // d'autant par rapport à son voisin.
    <span ref={zone} style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle', lineHeight: 0 }}>
      <button
        type="button"
        onClick={() => { if (!ouvert) ancrer(); setOuvert((o) => !o); }}
        aria-expanded={ouvert}
        aria-describedby={ouvert ? id : undefined}
        aria-label={`Qu’est-ce que « ${titre} » ?`}
        style={{
          // 24 px : le minimum d'une cible d'activation. Le rond visible fait
          // 16 px, la zone cliquable le déborde.
          width: 24, height: 24, minHeight: 24,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: 0, marginLeft: 'var(--espace-1)',
          border: 'none', background: 'none',
          // `inherit` et non une couleur figée : la bulle apparaît aussi dans
          // l'étiquette de la carte marge, qui est sombre. L'encre douce y
          // tombait à 1,74:1, un rond quasi invisible.
          color: 'inherit',
          lineHeight: 1,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 16, height: 16, borderRadius: '50%',
            border: `1px solid ${ouvert ? 'var(--couleur-accent)' : 'currentColor'}`,
            background: ouvert ? 'var(--couleur-accent)' : 'transparent',
            color: ouvert ? 'var(--couleur-sur-accent)' : 'currentColor',
            fontSize: 12, fontWeight: 700, fontStyle: 'italic',
            fontFamily: 'var(--police-titre)',
          }}
        >
          i
        </span>
      </button>

      {ouvert && (
        <span
          id={id}
          role="note"
          style={{
            position: 'absolute', top: '100%', zIndex: 40,
            [ancrage.cote]: 0,
            width: 'max-content', maxWidth: ancrage.largeurMax,
            marginTop: 'var(--espace-1)',
            padding: 'var(--espace-3)',
            background: 'var(--couleur-surface)',
            border: '1px solid var(--couleur-trait-fort)',
            borderRadius: 'var(--rayon)',
            boxShadow: '0 2px 12px rgb(27 35 51 / 14%)',
            // L'étiquette parente est souvent en capitales espacées : sans
            // remise à zéro, l'explication hériterait de sa casse.
            textTransform: 'none', letterSpacing: 'normal',
            fontFamily: 'var(--police-texte)', fontSize: '0.85rem', fontWeight: 400,
            color: 'var(--couleur-encre)', lineHeight: 1.45,
            whiteSpace: 'normal',
          }}
        >
          <strong style={{ display: 'block', marginBottom: 'var(--espace-1)' }}>{titre}</strong>
          {children}
        </span>
      )}
    </span>
  );
}
